/**
 * Payment Center Page
 *
 * A parent-friendly payment center showing:
 * - Outstanding balance summary
 * - Payment instructions (Zelle, check, cash)
 * - Payment history with status
 * - Links to confirm payments
 */

import {
  Badge,
  Box,
  Button,
  Card,
  Container,
  Flex,
  Grid,
  HStack,
  Heading,
  Icon,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useState } from "react"
import {
  FiAlertCircle,
  FiCheck,
  FiCheckCircle,
  FiClock,
  FiCopy,
  FiCreditCard,
  FiDollarSign,
  FiHome,
  FiMail,
  FiMapPin,
} from "react-icons/fi"

import { PaymentConfirmationModal } from "@/features/invoices/components"
import { useSubmitPaymentConfirmation } from "@/hooks/api/useInvoiceVerification"
import { type EnrichedInvoice, useInvoices } from "@/hooks/api/useInvoices"
import { usePaymentInstructions } from "@/hooks/api/usePaymentInstructions"
import { useCustomToast } from "@/hooks/useCustomToast"

// Format currency
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount)
}

// Format date
function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return "N/A"
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

// Copy to clipboard helper
function CopyButton({ text, label }: { text: string; label: string }) {
  const { showSuccessToast } = useCustomToast()
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      showSuccessToast(`${label} copied to clipboard`)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error("Failed to copy:", err)
    }
  }

  return (
    <Button
      size="sm"
      minH="44px"
      minW="44px"
      variant="ghost"
      onClick={handleCopy}
      color={copied ? "green.500" : "gray.500"}
    >
      {copied ? <FiCheck /> : <FiCopy />}
    </Button>
  )
}

// Payment method card component
function PaymentMethodCard({
  icon,
  title,
  children,
  highlight = false,
}: {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
  highlight?: boolean
}) {
  return (
    <Card.Root
      bg={highlight ? "green.50" : "white"}
      borderRadius="xl"
      boxShadow="sm"
      borderWidth="2px"
      borderColor={highlight ? "green.200" : "gray.100"}
    >
      <Card.Body p={5}>
        <VStack align="stretch" gap={3}>
          <HStack gap={3}>
            <Box
              p={2}
              bg={highlight ? "green.100" : "gray.100"}
              borderRadius="lg"
            >
              <Icon color={highlight ? "green.600" : "gray.600"} fontSize="xl">
                {icon}
              </Icon>
            </Box>
            <Text fontWeight="semibold" color="gray.800">
              {title}
            </Text>
            {highlight && (
              <Badge colorPalette="green" variant="subtle">
                Preferred
              </Badge>
            )}
          </HStack>
          {children}
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

// Invoice card for payment history
function InvoiceCard({
  invoice,
  onConfirmPayment,
}: {
  invoice: EnrichedInvoice & {
    verification_status?: string | null
  }
  onConfirmPayment: () => void
}) {
  const amountDue = invoice.amount_due ?? 0
  const amountPaid = invoice.amount_paid ?? 0
  const balance = amountDue - amountPaid
  const isPaid = balance <= 0
  const isPendingVerification =
    invoice.verification_status === "pending_verification"

  const getStatusConfig = () => {
    if (isPaid) {
      return { color: "green", icon: <FiCheckCircle />, label: "Paid" }
    }
    if (isPendingVerification) {
      return {
        color: "yellow",
        icon: <FiClock />,
        label: "Pending Verification",
      }
    }
    if (amountPaid > 0) {
      return {
        color: "orange",
        icon: <FiAlertCircle />,
        label: "Partial Payment",
      }
    }
    return { color: "red", icon: <FiAlertCircle />, label: "Payment Due" }
  }

  const status = getStatusConfig()

  return (
    <Card.Root
      bg="white"
      borderRadius="xl"
      boxShadow="sm"
      borderWidth="1px"
      borderColor="gray.100"
    >
      <Card.Body p={4}>
        <VStack align="stretch" gap={3}>
          <Flex justify="space-between" align="start">
            <Box>
              <Text fontWeight="semibold" color="gray.800">
                {invoice.student_name || "Enrollment"}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {formatDate(invoice.created_at)}
              </Text>
            </Box>
            <Badge
              colorPalette={status.color}
              variant="subtle"
              px={2}
              py={1}
              borderRadius="full"
            >
              <HStack gap={1}>
                <Icon fontSize="xs">{status.icon}</Icon>
                <Text>{status.label}</Text>
              </HStack>
            </Badge>
          </Flex>

          <Box bg="gray.50" borderRadius="lg" p={3}>
            <SimpleGrid columns={3} gap={2}>
              <Box>
                <Text fontSize="xs" color="gray.500">
                  Total
                </Text>
                <Text fontWeight="medium" color="gray.800">
                  {formatCurrency(amountDue)}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">
                  Paid
                </Text>
                <Text fontWeight="medium" color="green.600">
                  {formatCurrency(amountPaid)}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">
                  Balance
                </Text>
                <Text
                  fontWeight="bold"
                  color={isPaid ? "green.600" : "orange.600"}
                >
                  {formatCurrency(balance)}
                </Text>
              </Box>
            </SimpleGrid>
          </Box>

          {!isPaid && !isPendingVerification && (
            <Button colorPalette="green" size="sm" onClick={onConfirmPayment}>
              <FiCheckCircle />
              Confirm Payment Made
            </Button>
          )}

          {isPendingVerification && (
            <Box bg="yellow.50" borderRadius="md" p={3}>
              <HStack color="yellow.700" fontSize="sm">
                <FiClock />
                <Text>
                  Your payment confirmation is being reviewed. We'll update you
                  soon.
                </Text>
              </HStack>
            </Box>
          )}
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

export default function PaymentCenter() {
  const [confirmationInvoice, setConfirmationInvoice] = useState<
    (EnrichedInvoice & { verification_status?: string | null }) | null
  >(null)

  // Fetch data
  const { data: invoicesData, isLoading: invoicesLoading } = useInvoices()
  const {
    data: paymentInstructions,
    isLoading: instructionsLoading,
    error: instructionsError,
  } = usePaymentInstructions()

  const submitConfirmation = useSubmitPaymentConfirmation()
  const { showSuccessToast } = useCustomToast()

  const invoices = invoicesData?.items || []

  // Calculate totals
  const totalDue = invoices.reduce((sum, inv) => sum + (inv.amount_due ?? 0), 0)
  const totalPaid = invoices.reduce(
    (sum, inv) => sum + (inv.amount_paid ?? 0),
    0,
  )
  const totalBalance = totalDue - totalPaid

  // Separate invoices
  const unpaidInvoices = invoices.filter((inv) => {
    const balance = (inv.amount_due ?? 0) - (inv.amount_paid ?? 0)
    return balance > 0
  })
  const paidInvoices = invoices.filter((inv) => {
    const balance = (inv.amount_due ?? 0) - (inv.amount_paid ?? 0)
    return balance <= 0
  })

  const isLoading = invoicesLoading || instructionsLoading

  if (isLoading) {
    return (
      <Container maxW="5xl" py={8}>
        <VStack gap={4} py={16}>
          <Spinner size="xl" color="ui.main" />
          <Text color="gray.500">Loading payment information...</Text>
        </VStack>
      </Container>
    )
  }

  return (
    <Container maxW="5xl" py={8}>
      <VStack gap={8} align="stretch">
        {/* Header */}
        <Box>
          <Heading size="xl" color="gray.800" mb={2}>
            Payment Center
          </Heading>
          <Text color="gray.600">
            View your balance, make payments, and track payment history
          </Text>
        </Box>

        {/* Balance Summary */}
        <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
          <Card.Root
            bg={totalBalance > 0 ? "orange.50" : "green.50"}
            borderRadius="xl"
          >
            <Card.Body p={5}>
              <HStack>
                <Box p={3} bg="white" borderRadius="lg">
                  <Icon
                    color={totalBalance > 0 ? "orange.500" : "green.500"}
                    fontSize="xl"
                  >
                    <FiCreditCard />
                  </Icon>
                </Box>
                <Box>
                  <Text
                    fontSize="2xl"
                    fontWeight="bold"
                    color={totalBalance > 0 ? "orange.600" : "green.600"}
                  >
                    {formatCurrency(totalBalance)}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {totalBalance > 0 ? "Balance Due" : "All Paid"}
                  </Text>
                </Box>
              </HStack>
            </Card.Body>
          </Card.Root>

          <Card.Root bg="blue.50" borderRadius="xl">
            <Card.Body p={5}>
              <HStack>
                <Box p={3} bg="white" borderRadius="lg">
                  <Icon color="blue.500" fontSize="xl">
                    <FiDollarSign />
                  </Icon>
                </Box>
                <Box>
                  <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                    {formatCurrency(totalDue)}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Total Charges
                  </Text>
                </Box>
              </HStack>
            </Card.Body>
          </Card.Root>

          <Card.Root bg="green.50" borderRadius="xl">
            <Card.Body p={5}>
              <HStack>
                <Box p={3} bg="white" borderRadius="lg">
                  <Icon color="green.500" fontSize="xl">
                    <FiCheckCircle />
                  </Icon>
                </Box>
                <Box>
                  <Text fontSize="2xl" fontWeight="bold" color="green.600">
                    {formatCurrency(totalPaid)}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Total Paid
                  </Text>
                </Box>
              </HStack>
            </Card.Body>
          </Card.Root>
        </SimpleGrid>

        {/* Main Content Grid */}
        <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={8}>
          {/* Left Column - Payment Instructions */}
          <VStack align="stretch" gap={6}>
            <Heading size="md" color="gray.700">
              How to Pay
            </Heading>

            {instructionsError ? (
              <Card.Root bg="gray.50" borderRadius="xl">
                <Card.Body p={5}>
                  <Text color="gray.600">
                    Payment instructions are temporarily unavailable. Please
                    contact us for payment options.
                  </Text>
                </Card.Body>
              </Card.Root>
            ) : (
              <VStack align="stretch" gap={4}>
                {/* Zelle - Preferred */}
                <PaymentMethodCard
                  icon={<FiMail />}
                  title="Zelle (Instant)"
                  highlight
                >
                  <Box bg="white" borderRadius="lg" p={3}>
                    <Text fontSize="sm" color="gray.600" mb={1}>
                      Send payment to:
                    </Text>
                    <HStack>
                      <Text
                        fontWeight="bold"
                        color="green.700"
                        fontSize="lg"
                        fontFamily="mono"
                      >
                        {paymentInstructions?.zelle_email ||
                          "payments@flyingfishswim.com"}
                      </Text>
                      <CopyButton
                        text={
                          paymentInstructions?.zelle_email ||
                          "payments@flyingfishswim.com"
                        }
                        label="Email"
                      />
                    </HStack>
                  </Box>
                  <Text fontSize="sm" color="gray.600">
                    Include your swimmer's name in the memo. Payments are
                    typically verified within 24 hours.
                  </Text>
                </PaymentMethodCard>

                {/* Check */}
                <PaymentMethodCard icon={<FiCreditCard />} title="Check">
                  <Text fontSize="sm" color="gray.600">
                    Make checks payable to:
                  </Text>
                  <Text fontWeight="semibold" color="gray.800">
                    {paymentInstructions?.check_payable_to ||
                      "Flying Fish Swim School"}
                  </Text>
                  <Text fontSize="sm" color="gray.600" mt={2}>
                    Mail or bring to our office:
                  </Text>
                  <HStack align="start">
                    <Icon color="gray.400" mt={1}>
                      <FiMapPin />
                    </Icon>
                    <Text fontSize="sm" color="gray.700">
                      {paymentInstructions?.office_address ||
                        "123 Pool Lane, Swim City, CA 90210"}
                    </Text>
                  </HStack>
                </PaymentMethodCard>

                {/* Cash */}
                <PaymentMethodCard icon={<FiHome />} title="Cash (In-Person)">
                  <Text fontSize="sm" color="gray.600">
                    Pay in person at our office during business hours:
                  </Text>
                  <HStack align="start">
                    <Icon color="gray.400" mt={1}>
                      <FiClock />
                    </Icon>
                    <Text fontSize="sm" color="gray.700">
                      {paymentInstructions?.office_hours ||
                        "Mon-Fri 9am-5pm, Sat 9am-12pm"}
                    </Text>
                  </HStack>
                </PaymentMethodCard>
              </VStack>
            )}
          </VStack>

          {/* Right Column - Payment History */}
          <VStack align="stretch" gap={6}>
            <Heading size="md" color="gray.700">
              Your Invoices
            </Heading>

            {invoices.length === 0 ? (
              <Card.Root bg="gray.50" borderRadius="xl">
                <Card.Body p={8} textAlign="center">
                  <Icon color="gray.400" fontSize="3xl" mb={3}>
                    <FiCreditCard />
                  </Icon>
                  <Text fontWeight="medium" color="gray.700">
                    No invoices yet
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Invoices will appear here when you enroll in classes
                  </Text>
                </Card.Body>
              </Card.Root>
            ) : (
              <VStack align="stretch" gap={4}>
                {/* Unpaid invoices first */}
                {unpaidInvoices.length > 0 && (
                  <Box>
                    <Text
                      fontSize="sm"
                      fontWeight="medium"
                      color="orange.600"
                      mb={2}
                    >
                      Outstanding ({unpaidInvoices.length})
                    </Text>
                    <VStack align="stretch" gap={3}>
                      {unpaidInvoices.map((invoice) => (
                        <InvoiceCard
                          key={invoice.id}
                          invoice={invoice}
                          onConfirmPayment={() =>
                            setConfirmationInvoice(invoice)
                          }
                        />
                      ))}
                    </VStack>
                  </Box>
                )}

                {/* Paid invoices */}
                {paidInvoices.length > 0 && (
                  <Box>
                    <Text
                      fontSize="sm"
                      fontWeight="medium"
                      color="green.600"
                      mb={2}
                    >
                      Paid ({paidInvoices.length})
                    </Text>
                    <VStack align="stretch" gap={3}>
                      {paidInvoices.map((invoice) => (
                        <InvoiceCard
                          key={invoice.id}
                          invoice={invoice}
                          onConfirmPayment={() =>
                            setConfirmationInvoice(invoice)
                          }
                        />
                      ))}
                    </VStack>
                  </Box>
                )}
              </VStack>
            )}
          </VStack>
        </Grid>

        {/* Help section */}
        <Card.Root bg="ui.backgroundAlt" borderRadius="xl">
          <Card.Body p={5}>
            <HStack gap={4}>
              <Icon color="ui.main" fontSize="2xl">
                <FiAlertCircle />
              </Icon>
              <Box>
                <Text fontWeight="semibold" color="gray.800">
                  Need help with payments?
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Contact our office at{" "}
                  <Text as="span" fontWeight="medium">
                    {paymentInstructions?.zelle_email ||
                      "payments@flyingfishswim.com"}
                  </Text>{" "}
                  or call during office hours. We're happy to assist with
                  payment plans or questions.
                </Text>
              </Box>
            </HStack>
          </Card.Body>
        </Card.Root>
      </VStack>

      {/* Payment Confirmation Modal */}
      <PaymentConfirmationModal
        isOpen={!!confirmationInvoice}
        invoice={
          confirmationInvoice
            ? {
                id: confirmationInvoice.id,
                amount_due: confirmationInvoice.amount_due ?? 0,
                amount_paid: confirmationInvoice.amount_paid ?? 0,
                verification_status:
                  confirmationInvoice.verification_status ?? null,
                rejection_reason: null,
                rejection_notes: null,
              }
            : null
        }
        onClose={() => setConfirmationInvoice(null)}
        onSubmit={async (invoiceId, data) => {
          await submitConfirmation.mutateAsync({ invoiceId, data })
          setConfirmationInvoice(null)
          showSuccessToast(
            "Payment confirmation submitted. We'll verify it shortly.",
          )
        }}
        isSubmitting={submitConfirmation.isPending}
      />
    </Container>
  )
}
